import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Card, Flex, HelpIndicator, Label, RadioBar, Text, toast } from "@jsq/ds/core";
import { useFormContext } from "@jsq/ds/forms";
import { client } from "~/apollo/client";
import {
  ComplianceCaseTypeEnum,
  TaxDataSourceAction,
  TaxDataSourceAssignmentStatus,
  TaxDataSourceAssignmentType,
} from "~/graphql/supergraphEnums";
import {
  useGetTaxDataSourceAssignmentsQuery,
  useUpdateTaxDataSourceMutation,
} from "~/graphql/supergraphHooks";
import { type GetComplianceCaseQuery } from "~/graphql/supergraphTypes";
import { ONBOARDING_CASE_TYPES } from "~/pages/Compliance/CaseDetailsDrawer/PaymentInstructions/constants";
import { getInvestingProfileName } from "~/pages/Compliance/CaseDetailsDrawer/Profiles/utils";
import { useIOReusableProfiles } from "~/pages/Compliance/flags";
import { isFatcaCaseType } from "~/pages/Compliance/utils";
import { formatCountText } from "~/utils/strings";
import { DOCUMENT_TYPE_MAPPING } from "../constants";
import DateFieldFormValue from "../DocumentDetailsFormValues/ReusableFormValues/DateFieldFormValue";
import DocumentStatusRadioBar from "../DocumentDetailsFormValues/ReusableFormValues/DocumentStatusRadioBar";
import { WITHHOLDING_STATEMENT_SUBTYPE } from "../DocumentDetailsFormValues/WSeriesTaxDocFormValues/constants";
import { FormValues } from "../types";
import FatcaAssignmentModal from "./FatcaAssignmentModal";
import * as styles from "./styles_vanilla.css";
import UnassignmentModal from "./UnassignmentModal";
import ViewStatusModal from "./ViewStatusModal";

const DOCUMENT_STATUS_CARD_TYPES = [
  DOCUMENT_TYPE_MAPPING.wSeriesTaxForm.enumValue,
  DOCUMENT_TYPE_MAPPING.selfCertification.enumValue,
];

interface Props {
  readonly: boolean;
  complianceCase?: GetComplianceCaseQuery["complianceCase"];
  documentId?: number;
  documentName?: string;
  fatcaHasSelectionRef?: { current: boolean };
}

const DocumentStatusCard = ({
  readonly,
  complianceCase,
  documentId,
  documentName,
  fatcaHasSelectionRef,
}: Props) => {
  const profileId = complianceCase?.investingProfile?.id;
  const profileName = getInvestingProfileName(complianceCase?.investingProfile);
  const caseId = complianceCase?.id;
  const { values, submitCount, setFieldTouched } = useFormContext<FormValues>();
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [showViewStatusModal, setShowViewStatusModal] = useState(false);
  const [showUnassignmentModal, setShowUnassignmentModal] = useState(false);
  const [assignedOverrideState, setAssignedOverrideState] = useState<{
    documentId: number;
    value: boolean;
  } | null>(null);
  const mutatingDocumentIdRef = useRef<number | undefined>();

  const assignedOverride =
    assignedOverrideState && assignedOverrideState.documentId === documentId
      ? assignedOverrideState.value
      : null;

  const { data: assignmentsData, refetch: refetchAssignments } =
    useGetTaxDataSourceAssignmentsQuery({
      variables: { input: { documentId: documentId! } },
      skip: !documentId,
      fetchPolicy: "cache-and-network",
    });

  const assignments = assignmentsData?.taxDataSourceAssignments ?? [];
  const assignedRecords = assignments.filter(
    (a) => a.status === TaxDataSourceAssignmentStatus.Assigned,
  );
  const assignmentCount = assignedRecords.length;

  // Under IO reusable profiles a cloned case inherits assignments from earlier onboarding
  // cases without a record of its own, so the toggle stays empty until this case assigns or
  // unassigns and writes a self-referencing record. The "View status" count stays
  // document-wide, since every assigned position is still assigned.
  const ioReusableProfilesEnabled = useIOReusableProfiles();
  const scopeToCurrentCase =
    ioReusableProfilesEnabled &&
    complianceCase?.caseType !== undefined &&
    ONBOARDING_CASE_TYPES.includes(complianceCase.caseType);
  const isAssignedToCurrentCase = assignedRecords.some((a) => a.referencedCaseId === caseId);
  const hasOwnRecord = assignments.some(
    (a) => a.caseId === caseId && a.referencedCaseId === caseId,
  );

  const isAssigned = scopeToCurrentCase ? isAssignedToCurrentCase : assignmentCount > 0;
  const hasAnyRecord = scopeToCurrentCase ? hasOwnRecord : assignments.length > 0;
  const fatcaAssigned = assignedOverride ?? isAssigned;

  // When scoped, the toggle reflects only this case while unassignment still acts on every
  // assigned position, so the modal opens whenever any position remains assigned.
  const canUnassign = scopeToCurrentCase ? assignmentCount > 0 : fatcaAssigned;

  const radioBarValue: string | null =
    assignedOverride != null
      ? assignedOverride
        ? "assigned"
        : "not_assigned"
      : hasAnyRecord
        ? isAssigned
          ? "assigned"
          : "not_assigned"
        : null;

  const [updateTaxDataSource, { loading: isAssigning }] = useUpdateTaxDataSourceMutation({
    onCompleted: (data) => {
      const count = data.updateTaxDataSource.count;
      toast.success(`Assigned as data source to ${formatCountText(count, "position")}`);
      const mutatedDocId = mutatingDocumentIdRef.current;
      if (mutatedDocId) {
        setAssignedOverrideState({ documentId: mutatedDocId, value: true });
      }
      setShowAssignmentModal(false);
      void refetchAssignments();
      void client.refetchQueries({ include: ["IsProfileDirty"] });
    },
    onError: (e) => toast.error(e.message),
  });

  const [unassignTaxDataSource, { loading: isUnassigning }] = useUpdateTaxDataSourceMutation({
    onCompleted: (data) => {
      const count = data.updateTaxDataSource.count;
      toast.success(`Data source unassigned from ${formatCountText(count, "position")}`);
      const mutatedDocId = mutatingDocumentIdRef.current;
      if (mutatedDocId) {
        setAssignedOverrideState({ documentId: mutatedDocId, value: false });
      }
      setShowUnassignmentModal(false);
      void refetchAssignments();
      void client.refetchQueries({ include: ["IsProfileDirty"] });
    },
    onError: (e) => toast.error(e.message),
  });

  const isVisible = useMemo(
    () =>
      Boolean(
        values.documentType &&
        DOCUMENT_STATUS_CARD_TYPES.includes(values.documentType) &&
        values.secondaryDocumentType,
      ),
    [values.documentType, values.secondaryDocumentType],
  );

  const isWSeriesTaxForm =
    values.documentType === DOCUMENT_TYPE_MAPPING.wSeriesTaxForm.enumValue;
  const assignmentType = isWSeriesTaxForm
    ? TaxDataSourceAssignmentType.Fatca
    : TaxDataSourceAssignmentType.Crs;
  const assignmentLabel = isWSeriesTaxForm ? "FATCA" : "CRS";

  const isRootProfile =
    complianceCase?.investingProfile?.id !== undefined &&
    values.relatedProfile === String(complianceCase.investingProfile.id);
  const isWithholdingStatement = values.secondaryDocumentType === WITHHOLDING_STATEMENT_SUBTYPE;
  const fatcaCases = isFatcaCaseType(
    complianceCase?.caseType as ComplianceCaseTypeEnum | undefined,
  );
  const showAssignment = fatcaCases && isRootProfile && !isWithholdingStatement;

  const isAssignmentDisabled = readonly || !values.hasTaxDetails;
  const hasSelection = assignedOverride != null || hasAnyRecord;
  const assignmentErrorMessage =
    submitCount > 0 && !hasSelection && values.hasTaxDetails
      ? `${assignmentLabel} source document selection is required`
      : undefined;

  useEffect(() => {
    if (showAssignment && fatcaHasSelectionRef) {
      fatcaHasSelectionRef.current = hasSelection;
    }
  }, [hasSelection, showAssignment, fatcaHasSelectionRef]);

  useEffect(() => {
    if (submitCount > 0) {
      void setFieldTouched("date", true, false);
    }
  }, [submitCount, setFieldTouched]);

  const handleFatcaToggle = useCallback(
    (value: string | number) => {
      if (value === "assigned" && !readonly) {
        setShowAssignmentModal(true);
      } else if (value === "not_assigned") {
        if (canUnassign) {
          setShowUnassignmentModal(true);
        } else if (documentId) {
          setAssignedOverrideState({ documentId, value: false });
        }
      }
    },
    [readonly, canUnassign, documentId],
  );

  const handleAssign = useCallback(
    (positionIds: string[]) => {
      if (!documentId) return;
      mutatingDocumentIdRef.current = documentId;
      void updateTaxDataSource({
        variables: {
          input: {
            documentId,
            assignmentType,
            positionIds,
            action: TaxDataSourceAction.Assign,
          },
        },
      });
    },
    [documentId, assignmentType, updateTaxDataSource],
  );

  const handleUnassign = useCallback(
    (positionIds: string[]) => {
      if (!documentId) return;
      mutatingDocumentIdRef.current = documentId;
      void unassignTaxDataSource({
        variables: {
          input: {
            documentId,
            assignmentType,
            positionIds,
            action: TaxDataSourceAction.Unassign,
          },
        },
      });
    },
    [documentId, assignmentType, unassignTaxDataSource],
  );

  if (!isVisible) {
    return null;
  }

  return (
    <div className={styles.cardWrapper}>
      <Card>
        <Text variant="secondary-md" className={styles.cardTitle}>
          DOCUMENT STATUS
        </Text>
        <div className={styles.dateFieldWrapper}>
          <DateFieldFormValue
            name="date"
            label="Date of signature"
            readonly={readonly}
            maxDate={new Date()}
            showExpiredWarning={isWSeriesTaxForm}
            hasIcon
            isFull
            placeholderText="MM/DD/YYYY"
          />
        </div>
        {showAssignment && (
          <>
            <Flex
              justifyContent="space-between"
              alignItems="center"
              className={styles.fatcaLabelRow}
            >
              <Label>{assignmentLabel} source document</Label>
              {assignmentCount === 0 ? (
                <HelpIndicator
                  id="view-fatca-status-tooltip"
                  placement="top"
                  fullWidth
                  content={
                    <Text variant="regular-md" className={styles.tooltipText}>
                      {`Currently, there are no positions linked to this document as the ${assignmentLabel} source document.`}
                    </Text>
                  }
                >
                  <Button
                    displayAsLink
                    variant="link"
                    data-testid="view-fatca-status"
                    onClick={() => setShowViewStatusModal(true)}
                    disabled
                  >
                    View status ({assignmentCount})
                  </Button>
                </HelpIndicator>
              ) : (
                <Button
                  displayAsLink
                  variant="link"
                  data-testid="view-fatca-status"
                  onClick={() => setShowViewStatusModal(true)}
                  disabled={readonly}
                >
                  View status ({assignmentCount})
                </Button>
              )}
            </Flex>
            <div className={styles.fatcaRadioWrapper}>
              {!values.hasTaxDetails && !readonly ? (
                <HelpIndicator
                  id="fatca-assignment-tooltip"
                  placement="top"
                  fullWidth
                  content={
                    <Text variant="regular-md" className={styles.tooltipText}>
                      {`${assignmentLabel} details are required to assign as a ${assignmentLabel} source document.`}
                    </Text>
                  }
                >
                  <div>
                    <RadioBar
                      size="small"
                      isFullWidth
                      value={radioBarValue}
                      onSelectOption={handleFatcaToggle}
                      errorMessage={assignmentErrorMessage}
                      options={[
                        { label: "Assigned", value: "assigned", isDisabled: true },
                        { label: "Not assigned", value: "not_assigned", isDisabled: true },
                      ]}
                    />
                  </div>
                </HelpIndicator>
              ) : (
                <RadioBar
                  size="small"
                  isFullWidth
                  value={radioBarValue}
                  onSelectOption={handleFatcaToggle}
                  errorMessage={assignmentErrorMessage}
                  options={[
                    { label: "Assigned", value: "assigned", isDisabled: isAssignmentDisabled },
                    {
                      label: "Not assigned",
                      value: "not_assigned",
                      isDisabled: isAssignmentDisabled,
                    },
                  ]}
                />
              )}
            </div>
          </>
        )}
        <Label>Document status</Label>
        <DocumentStatusRadioBar readonly={readonly} isFull />
      </Card>
      {showAssignmentModal && profileId && documentId && caseId && (
        <FatcaAssignmentModal
          profileId={profileId}
          profileName={profileName ?? ""}
          documentName={documentName ?? ""}
          documentType={values.documentType ?? ""}
          caseId={caseId}
          onClose={() => setShowAssignmentModal(false)}
          onAssign={handleAssign}
          isAssigning={isAssigning}
        />
      )}
      {showViewStatusModal && profileId && documentId && caseId && (
        <ViewStatusModal
          profileId={profileId}
          profileName={profileName ?? ""}
          documentName={documentName ?? ""}
          documentType={values.documentType ?? ""}
          documentId={documentId}
          caseId={caseId}
          onClose={() => setShowViewStatusModal(false)}
        />
      )}
      {showUnassignmentModal && profileId && documentId && caseId && (
        <UnassignmentModal
          profileId={profileId}
          profileName={profileName ?? ""}
          documentType={values.documentType ?? ""}
          documentId={documentId}
          caseId={caseId}
          onClose={() => setShowUnassignmentModal(false)}
          onUnassign={handleUnassign}
          isUnassigning={isUnassigning}
        />
      )}
    </div>
  );
};

export { DocumentStatusCard };
